import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TaisaCoreLogo } from '../ui';
import clinicConfig from '../../config/clinic';
import './Sidebar.css';

const navSections = {
  he: [
    {
      label: 'ראשי',
      items: [
        { id: 'dashboard', path: '/', label: 'דשבורד', icon: '📊' },
        { id: 'calendar', path: '/calendar', label: 'לוח תיאום', icon: '📅' },
        { id: 'action-needed', path: '/settings?tab=kb', label: 'דרוש טיפול', icon: '📥' },
        { id: 'waiting-room', path: '/waiting-room', label: 'חדר המתנה', icon: '⏱️' },
        { id: 'patients', path: '/patients', label: 'מטופלים', icon: '👥' },
      ],
    },
    {
      label: 'ניהול',
      items: [
        { id: 'recalls', path: '/recalls', label: 'תזכורות', icon: '🔔' },
        { id: 'billing', path: '/billing', label: 'חיוב', icon: '💳' },
        { id: 'inventory', path: '/inventory', label: 'מלאי', icon: '📦' },
        { id: 'tasks', path: '/tasks', label: 'משימות', icon: '✅' },
      ],
    },
    {
      label: 'כלים',
      items: [
        { id: 'reports', path: '/reports', label: 'דוחות', icon: '📈' },
        // TODO: restore AIHub nav after voice/sessions worker-kill fix lands (Dev Agent)
        // { id: 'ai-hub', path: '/ai-hub', label: 'מרכז AI', icon: '🤖' },
        { id: 'medications', path: '/medications', label: 'תרופות', icon: '💊' },
        { id: 'procedures', path: '/procedures', label: 'פרוצדורות', icon: '🦷' },
      ],
    },
  ],
  en: [
    {
      label: 'Main',
      items: [
        { id: 'dashboard', path: '/', label: 'Dashboard', icon: '📊' },
        { id: 'calendar', path: '/calendar', label: 'Calendar', icon: '📅' },
        { id: 'action-needed', path: '/settings?tab=kb', label: 'Action Needed', icon: '📥' },
        { id: 'waiting-room', path: '/waiting-room', label: 'Waiting Room', icon: '⏱️' },
        { id: 'patients', path: '/patients', label: 'Patients', icon: '👥' },
      ],
    },
    {
      label: 'Management',
      items: [
        { id: 'recalls', path: '/recalls', label: 'Recalls', icon: '🔔' },
        { id: 'billing', path: '/billing', label: 'Billing', icon: '💳' },
        { id: 'inventory', path: '/inventory', label: 'Inventory', icon: '📦' },
        { id: 'tasks', path: '/tasks', label: 'Tasks', icon: '✅' },
      ],
    },
    {
      label: 'Tools',
      items: [
        { id: 'reports', path: '/reports', label: 'Reports', icon: '📈' },
        // TODO: restore AIHub nav after voice/sessions worker-kill fix lands (Dev Agent)
        // { id: 'ai-hub', path: '/ai-hub', label: 'AI Hub', icon: '🤖' },
        { id: 'medications', path: '/medications', label: 'Medications', icon: '💊' },
        { id: 'procedures', path: '/procedures', label: 'Procedures', icon: '🦷' },
      ],
    },
  ],
};

const systemItems = {
  he: [
    { id: 'settings', path: '/settings', label: 'הגדרות', icon: '⚙️' },
    { id: 'qa', path: '/qa', label: 'QA', icon: '🧪' },
  ],
  en: [
    { id: 'settings', path: '/settings', label: 'Settings', icon: '⚙️' },
    { id: 'qa', path: '/qa', label: 'QA', icon: '🧪' },
  ],
};

const ACTION_NEEDED_POLL_MS = 60000;

const Sidebar = ({ language = 'he', collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) => {
  const sections = navSections[language] || navSections.he;
  const system = systemItems[language] || systemItems.he;
  const location = useLocation();
  const { authFetch, isAuthenticated } = useAuth();
  const [actionNeededCount, setActionNeededCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    const loadCount = async () => {
      try {
        const res = await authFetch('/api/clinic/unanswered?status=pending');
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setActionNeededCount(Array.isArray(data) ? data.length : 0);
        }
      } catch (e) {
        // Badge is best-effort; keep the last known count on transient errors
      }
    };
    loadCount();
    const timer = setInterval(loadCount, ACTION_NEEDED_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [authFetch, isAuthenticated]);

  // NavLink's isActive ignores the query string, which conflates
  // "Action Needed" (/settings?tab=kb) with plain Settings.
  const isItemActive = (item, navLinkActive) => {
    if (item.path.includes('?')) {
      const [pathname, query] = item.path.split('?');
      return location.pathname === pathname && location.search.includes(query);
    }
    if (item.path === '/settings') {
      return navLinkActive && !location.search.includes('tab=kb');
    }
    return navLinkActive;
  };

  const handleNavClick = () => {
    if (mobileOpen && onCloseMobile) {
      onCloseMobile();
    }
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={onCloseMobile} />
      )}

      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-logo">
            <TaisaCoreLogo size="tiny" animated={false} withBackground={false} />
          </div>
          {!collapsed && (
            <div className="sidebar-brand-text">
              <span className="sidebar-clinic-name">{clinicConfig.name}</span>
              <span className="sidebar-platform">
                powered by <strong>{clinicConfig.platform}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {sections.map((section) => (
            <div key={section.label} className="sidebar-section">
              {!collapsed && (
                <span className="sidebar-section-label">{section.label}</span>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `sidebar-item ${isItemActive(item, isActive) ? 'sidebar-item--active' : ''}`
                  }
                  onClick={handleNavClick}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="sidebar-item-icon">{item.icon}</span>
                  {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
                  {item.id === 'action-needed' && actionNeededCount > 0 && (
                    <span className="sidebar-item-count" aria-label={`${actionNeededCount}`}>
                      {actionNeededCount > 99 ? '99+' : actionNeededCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* System section at bottom */}
        <div className="sidebar-footer">
          <div className="sidebar-section">
            {!collapsed && (
              <span className="sidebar-section-label">
                {language === 'he' ? 'מערכת' : 'System'}
              </span>
            )}
            {system.map((item) => (
              <NavLink
                key={item.id}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-item ${isItemActive(item, isActive) ? 'sidebar-item--active' : ''}`
                }
                onClick={handleNavClick}
                title={collapsed ? item.label : undefined}
              >
                <span className="sidebar-item-icon">{item.icon}</span>
                {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
              </NavLink>
            ))}
          </div>

          {/* Collapse toggle */}
          <button
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            title={collapsed ? (language === 'he' ? 'הרחב' : 'Expand') : (language === 'he' ? 'כווץ' : 'Collapse')}
          >
            <span className={`sidebar-collapse-icon ${collapsed ? 'sidebar-collapse-icon--collapsed' : ''}`}>
              {'\u276E'}
            </span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
